import { useState, type FormEvent, type ReactElement } from "react";
import { signIn, signUp } from "../../auth";

interface AuthFormProps {
	mode: "login" | "signup";
	onSuccess?: () => void;
	switchUrl?: string;
	redirectPath?: string;
}

export function AuthForm({
	mode,
	onSuccess,
	switchUrl,
	redirectPath,
}: AuthFormProps): ReactElement {
	const isLogin = mode === "login";
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	function buttonLabel(): string {
		if (isLogin) return loading ? "Signing in..." : "Sign in";
		return loading ? "Creating account..." : "Create account";
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);

		try {
			const { error: authError } = isLogin
				? await signIn.email({ email, password })
				: await signUp.email({ name, email, password });

			if (authError) {
				setError(
					authError.message ||
						(isLogin
							? "Failed to sign in"
							: "Failed to create account"),
				);
			} else {
				onSuccess?.();
			}
		} catch {
			setError("An unexpected error occurred");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="bg-surface text-fg flex min-h-screen flex-col items-center justify-center px-6">
			<div className="w-full max-w-sm">
				<h1 className="font-display mb-8 text-center text-2xl">
					{isLogin ? "Sign in to clearvoid" : "Sign up for clearvoid"}
				</h1>

				<button
					type="button"
					onClick={() => signIn.google(redirectPath)}
					className="border-line-strong bg-surface-raised hover:bg-surface-hover hover:border-line-emphasis flex w-full items-center justify-center gap-2 rounded-md border px-4 py-3 text-base font-medium transition-colors"
				>
					<svg width="18" height="18" viewBox="0 0 24 24">
						<path
							fill="#4285F4"
							d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
						/>
						<path
							fill="#34A853"
							d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
						/>
						<path
							fill="#FBBC05"
							d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
						/>
						<path
							fill="#EA4335"
							d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
						/>
					</svg>
					Continue with Google
				</button>

				<div className="text-fg-ghost my-6 flex items-center gap-4 text-sm">
					<div className="bg-line h-px flex-1" />
					<span>or</span>
					<div className="bg-line h-px flex-1" />
				</div>

				<form className="flex flex-col gap-4" onSubmit={handleSubmit}>
					{error && (
						<div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
							{error}
						</div>
					)}

					{!isLogin && (
						<input
							type="text"
							placeholder="Name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							className="border-line-strong bg-surface-raised placeholder:text-fg-ghost focus:border-accent rounded-md border px-4 py-3 text-base focus:outline-none"
						/>
					)}
					<input
						type="email"
						placeholder="Email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						required
						data-testid="auth-email-input"
						className="border-line-strong bg-surface-raised placeholder:text-fg-ghost focus:border-accent rounded-md border px-4 py-3 text-base focus:outline-none"
					/>
					<input
						type="password"
						placeholder="Password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						required
						{...(!isLogin && { minLength: 8 })}
						data-testid="auth-password-input"
						className="border-line-strong bg-surface-raised placeholder:text-fg-ghost focus:border-accent rounded-md border px-4 py-3 text-base focus:outline-none"
					/>
					<button
						type="submit"
						disabled={loading}
						data-testid="auth-submit-btn"
						className="bg-accent hover:bg-accent-dim text-on-accent rounded-md px-4 py-3 text-base font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70"
					>
						{buttonLabel()}
					</button>
				</form>

				<p className="text-fg-muted mt-6 text-center text-sm">
					{isLogin
						? "Don't have an account? "
						: "Already have an account? "}
					<a
						href={switchUrl ?? (isLogin ? "/signup" : "/login")}
						className="text-accent hover:text-accent-dim no-underline"
					>
						{isLogin ? "Sign up" : "Sign in"}
					</a>
				</p>
			</div>
		</div>
	);
}
